import React, { useEffect, useState } from "react";
import useFetch from "hooks/useFetch";
import DailyTvPoster from "./DailyTvPoster";
import { Badge, Button, Icon, PageHeader, useToast } from "stories/Admin";
import {
  DailyCard,
  DailyHistory,
  DailySkeleton,
  DismissibleError,
  formatShortDate,
} from "./DailyView";

interface HadithSource {
  collection: string;
  book: string;
  chapter: string;
  hadith_number: string;
  grading: string;
  reference_note: string;
}

interface Hadith {
  arabic: string;
  norwegian: string;
  source: HadithSource;
}

interface AIHadith {
  id: number;
  date_iso: string;
  topic_tags: string[];
  hadith: Hadith;
  explanation_norwegian: string;
  action_points: string[];
  fiqh_controversy_risk: string;
  confidence: number;
  cached: boolean;
  cacheTimestamp: string;
}

interface HadithResponse {
  success: boolean;
  data: AIHadith;
  error?: string;
  generatedAt: string;
}

const HadithPage: React.FC = () => {
  const { execute } = useFetch();
  const toast = useToast();
  const [allHadiths, setAllHadiths] = useState<AIHadith[]>([]);
  const [todayHadith, setTodayHadith] = useState<AIHadith | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAllHadiths = async () => {
    try {
      const result = (await execute(
        "/api/v1/ai-hadith/all",
        "GET",
      )) as AIHadith[];
      if (result) {
        setAllHadiths(result);
      }
    } catch (err) {
      console.error("Error fetching all hadiths:", err);
    }
  };

  const fetchTodayHadith = async () => {
    try {
      const result = (await execute(
        "/api/v1/ai-hadith",
        "GET",
      )) as HadithResponse;
      if (result && result.success && result.data) {
        setTodayHadith(result.data);
      }
    } catch (err) {
      console.error("Error fetching today's hadith:", err);
    }
  };

  const regenerateTodayHadith = async () => {
    setRegenerating(true);
    setError(null);
    try {
      const result = (await execute(
        "/api/v1/ai-hadith/regenerate",
        "POST",
      )) as HadithResponse;
      if (result && result.success && result.data) {
        setTodayHadith(result.data);
        // Refresh all hadiths list
        await fetchAllHadiths();
        toast.success("Dagens hadith er generert på nytt.");
      } else if (result && result.error) {
        setError(result.error);
      }
    } catch (err) {
      console.error("Error regenerating hadith:", err);
      setError("Kunne ikke generere hadith på nytt. Prøv igjen senere.");
    } finally {
      setRegenerating(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchTodayHadith(), fetchAllHadiths()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  const header = (
    <PageHeader
      title="Dagens hadith"
      subtitle="AI-generert hadith som vises på Min side i appen."
      actions={
        <Button
          onClick={regenerateTodayHadith}
          loading={regenerating}
          icon={<Icon name="spark" size={16} strokeWidth={2} />}
        >
          {regenerating ? "Genererer …" : "Generer dagens hadith på nytt"}
        </Button>
      }
    />
  );

  if (loading) {
    return (
      <>
        {header}
        <DailySkeleton />
      </>
    );
  }

  return (
    <>
      {header}

      <DismissibleError message={error} onClose={() => setError(null)} />

      {todayHadith && (
        <DailyCard
          title="Dagens hadith"
          badges={
            <>
              <Badge tone="grey">{formatShortDate(todayHadith.date_iso)}</Badge>
              <Badge tone="blue">{todayHadith.hadith.source.collection}</Badge>
              {todayHadith.hadith.source.hadith_number && (
                <Badge tone="grey">
                  #{todayHadith.hadith.source.hadith_number}
                </Badge>
              )}
              {todayHadith.hadith.source.grading && (
                <Badge tone="mint">{todayHadith.hadith.source.grading}</Badge>
              )}
            </>
          }
          meta={
            <>
              {todayHadith.cached ? "Fra cache" : "Nylig generert"}
              {!!todayHadith.confidence &&
                ` · Konfidens ${Math.round(todayHadith.confidence * 100)} %`}
            </>
          }
          arabic={todayHadith.hadith.arabic}
          norwegian={todayHadith.hadith.norwegian}
          explanation={todayHadith.explanation_norwegian}
          actionPoints={todayHadith.action_points}
          tags={todayHadith.topic_tags}
        />
      )}

      <DailyTvPoster kind="HADITH" refreshKey={todayHadith?.id} />

      <DailyHistory
        title={`Alle genererte hadither (${allHadiths.length})`}
        emptyIcon="star"
        emptyTitle="Ingen hadither ennå"
        emptyText="Trykk «Generer dagens hadith på nytt» for å lage den første."
        rows={allHadiths.map((hadith) => ({
          key: hadith.id,
          date: hadith.date_iso,
          text: hadith.hadith.norwegian,
          arabic: hadith.hadith.arabic,
          confidence: hadith.confidence,
          source: [
            hadith.hadith.source.collection,
            hadith.hadith.source.hadith_number,
            hadith.hadith.source.grading && `(${hadith.hadith.source.grading})`,
          ]
            .filter(Boolean)
            .join(" "),
        }))}
      />
    </>
  );
};

export default HadithPage;
